import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const EditableTable = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [newSickLeave, setNewSickLeave] = useState("");
  const [newWeekOff, setNewWeekOff] = useState("");
  const [weekRange, setWeekRange] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get("https://anltcs.noorride.com/driver-data")
      .then((response) => {
        setData(response.data);
        setFilteredData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });

    if (!localStorage.getItem("isAuthenticated")) {
      navigate("/");
    }

    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + startOffset);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    setWeekRange(
      `${startDate.toISOString().slice(0, 10)} to ${endDate.toISOString().slice(0, 10)}`
    );
    setCurrentDate(today.toISOString().slice(0, 10));
  }, []);

  const handleEditClick = (id, row) => {
    setEditRowId(id);
    setEditedData({
      ...row,
      weekOffs: row.weekOffs || [],
      shiftTime: row.shiftTime || "",
      sickLeaves: row.sickLeaves || [],
    });
  };

  const handleInputChange = (e, field) => {
    setEditedData({ ...editedData, [field]: e.target.value });
  };

  const handleResetClick = (row) => {
    setEditedData({
      ...row,
      weekOffs: row.weekOffs || [],
      sickLeaves: row.sickLeaves || [],
      shiftTime: row.shiftTime || "",
      shiftChangeLocation: row.shiftChangeLocation || "",
      shiftChangePerson: row.shiftChangePerson || "",
    });
  };

  const handleAddWeekOff = () => {
    const currentWeekOffs = editedData.weekOffs || [];
    if (newWeekOff && !currentWeekOffs.includes(newWeekOff)) {
      setEditedData({
        ...editedData,
        weekOffs: [...currentWeekOffs, newWeekOff],
      });
      setNewWeekOff("");
    } else if (currentWeekOffs.includes(newWeekOff)) {
      alert("This week off is already added!");
    }
  };

  const handleRemoveWeekOff = (day) => {
    setEditedData({
      ...editedData,
      weekOffs: editedData.weekOffs.filter((d) => d !== day),
    });
  };

  const handleAddSickLeave = () => {
    if (newSickLeave && !editedData.sickLeaves.includes(newSickLeave)) {
      setEditedData({
        ...editedData,
        sickLeaves: [...editedData.sickLeaves, newSickLeave],
      });
      setNewSickLeave("");
    } else if (editedData.sickLeaves.includes(newSickLeave)) {
      alert("This sick leave date is already added!");
    }
  };

  const handleShiftTimeChange = (e) => {
    setEditedData({ ...editedData, shiftTime: e.target.value });
  };

  const handleSaveClick = (id) => {
    axios
      .put(`https://anltcs.noorride.com/drivers-update/${id}`, editedData)
      .then(() => {
        setData((prevData) =>
          prevData.map((item) => (item._id === id ? editedData : item))
        );
        setFilteredData((prevData) =>
          prevData.map((item) => (item._id === id ? editedData : item))
        );
        setEditRowId(null);
      })
      .catch((error) => {
        console.error("Error saving data:", error);
      });
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredData(
      data.filter((row) =>
        row.firstName.toLowerCase().includes(query)
      )
    );
  };

  const generateWeekDays = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + startOffset);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return Array.from({ length: 7 }, (_, i) => {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const day = daysOfWeek[currentDate.getDay()];
      const date = currentDate.getDate().toString().padStart(2, "0");
      const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
      return {
        full: day,
        name: day,
        display: `${date}/${month} - ${day}`,
      };
    });
  };

  const weekDays = generateWeekDays();

  const formatWeekOffDisplay = (day) => {
    const matchingDay = weekDays.find((d) => d.name === day);
    return matchingDay ? matchingDay.display : day;
  };

  const shiftTimes = ["0-12", "6-18", "18-6", "6-21", "8-20", "9-21", "21-9", "12-24", "NO"];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Driver Management Dashboard</h1>
        <div style={styles.buttonContainer}>
          <button
            onClick={() => navigate("/leaveDashboard")}
            style={styles.leaveDashboard}
          >
            Leave Management Dashboard
          </button>
          <div style={styles.rightButtons}>
            <button
              onClick={() => navigate("/overtime")}
              style={styles.leaveDashboard}
            >
              Overtime Dashboard
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("isAuthenticated");
                navigate("/");
              }}
              style={styles.logout}
            >
              Logout
            </button>
          </div>
        </div>
        <div style={styles.dateInfo}>
          <span style={styles.currentDate}>Present Day: {currentDate}</span>
          <span style={styles.weekRange}>Current Week: {weekRange}</span>
        </div>
      </div>

      <input
        type="text"
        placeholder="Search by name"
        value={searchQuery}
        onChange={handleSearch}
        style={styles.searchBar}
      />

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>First Name</th>
            <th style={styles.tableHeader}>Email</th>
            <th style={styles.tableHeader}>Phone Number</th>
            <th style={styles.tableHeader}>Week Offs</th>
            <th style={styles.tableHeader}>Unpaid Leaves</th>
            <th style={styles.tableHeader}>ShiftTime</th>
            <th style={styles.tableHeader}>Shift Change Location</th>
            <th style={styles.tableHeader}>Shift Change Person</th>
            <th style={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((row) => (
            <tr
              key={row._id}
              style={
                editRowId === row._id
                  ? { ...styles.tableRow, ...styles.editingRow }
                  : styles.tableRow
              }
            >
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <input
                    type="text"
                    value={editedData.firstName || ""}
                    onChange={(e) => handleInputChange(e, "firstName")}
                    style={styles.input}
                  />
                ) : (
                  row.firstName
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <input
                    type="email"
                    value={editedData.email || ""}
                    onChange={(e) => handleInputChange(e, "email")}
                    style={styles.input}
                  />
                ) : (
                  row.email
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <input
                    type="text"
                    value={editedData.phoneNumber || ""}
                    onChange={(e) => handleInputChange(e, "phoneNumber")}
                    style={styles.input}
                  />
                ) : (
                  row.phoneNumber
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <div style={styles.checkboxContainer}>
                    <div style={styles.selectedList}>
                      {editedData.weekOffs &&
                        editedData.weekOffs.map((day) => {
                          const formattedDay = formatWeekOffDisplay(day);
                          return (
                            <div key={day} style={styles.selectedDay}>
                              {formattedDay}
                              <span
                                onClick={() => handleRemoveWeekOff(day)}
                                style={styles.removeIcon}
                              >
                                ❌
                              </span>
                            </div>
                          );
                        })}
                    </div>
                    <select
                      value={newWeekOff}
                      onChange={(e) => setNewWeekOff(e.target.value)}
                      style={styles.selectDropdown}
                    >
                      <option value="">Select Day</option>
                      {weekDays.map((day) => (
                        <option key={day.full} value={day.name}>
                          {day.display}
                        </option>
                      ))}
                    </select>
                    <button onClick={handleAddWeekOff} style={styles.addButton}>
                      Add Week Off
                    </button>
                  </div>
                ) : (
                  (row.weekOffs || []).map((day) => formatWeekOffDisplay(day)).join(", ")
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <div>
                    <ul style={styles.selectedList}>
                      {editedData.sickLeaves.map((date) => (
                        <li key={date} style={styles.listItem}>
                          {date}
                          <span
                            onClick={() =>
                              setEditedData({
                                ...editedData,
                                sickLeaves: editedData.sickLeaves.filter(
                                  (d) => d !== date
                                ),
                              })
                            }
                            style={styles.removeIcon}
                          >
                            ❌
                          </span>
                        </li>
                      ))}
                    </ul>
                    <input
                      type="date"
                      value={newSickLeave}
                      onChange={(e) => setNewSickLeave(e.target.value)}
                      style={styles.input}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    <button onClick={handleAddSickLeave} style={styles.addButton}>
                      Add Leaves
                    </button>
                  </div>
                ) : (
                  (row.sickLeaves || []).join(", ")
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <select
                    value={editedData.shiftTime || ""}
                    onChange={handleShiftTimeChange}
                    style={styles.selectDropdown}
                  >
                    <option value="">Select Shift Time</option>
                    {shiftTimes.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                ) : (
                  row.shiftTime || "Not added"
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <input
                    type="text"
                    value={editedData.shiftChangeLocation || ""}
                    onChange={(e) => handleInputChange(e, "shiftChangeLocation")}
                    style={styles.input}
                  />
                ) : (
                  row.shiftChangeLocation || "Not Set"
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={editedData.shiftChangePerson || ""}
                      onChange={(e) => {
                        const query = e.target.value.toLowerCase();
                        setEditedData({ ...editedData, shiftChangePerson: query });

                        const suggestions = data
                          .map((driver) => driver.email)
                          .filter((email) => email.toLowerCase().includes(query));

                        setEmailSuggestions(suggestions);
                      }}
                      style={styles.input}
                    />
                    {emailSuggestions.length > 0 && (
                      <ul style={styles.suggestionsDropdown}>
                        {emailSuggestions.map((email) => (
                          <li
                            key={email}
                            onClick={() => {
                              setEditedData({ ...editedData, shiftChangePerson: email });
                              setEmailSuggestions([]);
                            }}
                            style={styles.suggestionItem}
                          >
                            {email}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  row.shiftChangePerson || "None"
                )}
              </td>
              <td style={styles.tableCell}>
                {editRowId === row._id ? (
                  <div>
                    <button
                      style={styles.saveButton}
                      onClick={() => handleSaveClick(row._id)}
                    >
                      Save
                    </button>
                    <button
                      style={styles.resetButton}
                      onClick={() => handleResetClick(row)}
                    >
                      Reset
                    </button>
                  </div>
                ) : (
                  <button
                    style={styles.editButton}
                    onClick={() => handleEditClick(row._id, row)}
                  >
                    Edit
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const styles = {
  container: {
    margin: "20px auto",
    padding: "40px",
    maxWidth: "1600px",
    fontFamily: "'Roboto', sans-serif",
    backgroundColor: "#f9fafb",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    textAlign: "center",
    marginBottom: "30px",
    fontSize: "32px",
    color: "#2d3748",
    fontWeight: "600",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  rightButtons: {
    display: "flex",
    gap: "10px",
  },
  leaveDashboard: {
    backgroundColor: "#4299e1",
    color: "white",
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    transition: "background-color 0.3s",
  },
  logout: {
    backgroundColor: "#f56565",
    color: "white",
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    transition: "background-color 0.3s",
  },
  dateInfo: {
    textAlign: "left",
    fontSize: "16px",
    marginBottom: "20px",
  },
  currentDate: {
    fontWeight: "bold",
    fontSize: "20px",
    color: "#4a5568",
    display: "block",
  },
  weekRange: {
    fontWeight: "bold",
    fontSize: "20px",
    color: "#4a5568",
    marginTop: "5px",
  },
  searchBar: {
    width: "100%",
    padding: "12px",
    marginBottom: "20px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.3s",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  tableHeader: {
    backgroundColor: "#4299e1",
    color: "white",
    padding: "16px",
    textAlign: "left",
    fontWeight: "500",
  },
  tableRow: {
    borderBottom: "1px solid #e2e8f0",
  },
  editingRow: {
    backgroundColor: "#ebf8ff",
  },
  tableCell: {
    padding: "16px",
    textAlign: "left",
  },
  input: {
    width: "95%",
    padding: "10px",
    margin: "6px 0",
    boxSizing: "border-box",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.3s",
  },
  selectedList: {
    margin: "0",
    padding: "0",
    listStyle: "none",
    marginBottom: "10px",
  },
  selectDropdown: {
    width: "100%",
    padding: "10px",
    margin: "6px 0",
    boxSizing: "border-box",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.3s",
  },
  addButton: {
    padding: "10px 14px",
    backgroundColor: "#48bb78",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    marginLeft: "5px",
    transition: "background-color 0.3s",
  },
  resetButton: {
    backgroundColor: "#f56565",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  saveButton: {
    backgroundColor: "#4299e1",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    marginRight: "10px",
    marginBottom: "10px",
    transition: "background-color 0.3s",
  },
  editButton: {
    backgroundColor: "#4299e1",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  suggestionsDropdown: {
    position: "absolute",
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    width: "95%",
    zIndex: 10,
    maxHeight: "150px",
    overflowY: "auto",
    marginTop: "5px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  suggestionItem: {
    padding: "8px",
    cursor: "pointer",
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.3s",
  },
  selectedDay: {
    display: "inline-block",
    backgroundColor: "#edf2f7",
    padding: "6px 12px",
    borderRadius: "8px",
    margin: "4px",
    fontSize: "14px",
  },
  removeIcon: {
    cursor: "pointer",
    marginLeft: "8px",
    color: "#f56565",
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px",
    borderBottom: "1px solid #e2e8f0",
  },
};

export default EditableTable;